import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtilsService, QueryParamsModel, QueryResultsModel} from '../_base/crud';
import {PROJECT_STATUS_ARCHIVED, PROJECT_STATUS_NAMES, ProjectModel, ProjectStatus} from '../models/project.model';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import moment from 'moment';
import {SPACE_TYPE_SUUMO} from '../models/space.model';
import { OKResponse } from '../_base/crud/models/query-models/query-results.model';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { PREFFERED_TIME_PRIORITIES, ScheduleModel, studio55Logo } from '../models/schedule.model';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { FileModel } from '../models/file.model';

const API_PROJECTS_URL = `${environment.pathToAPI}/api/v1/projects`;

@Injectable()
export class ProjectsService {
    constructor(
        private http: HttpClient,
        private httpUtils: HttpUtilsService,
        private datePipe: DatePipe,
        private translateService: TranslateService) {
    }

    createProject(project: ProjectModel): Observable<ProjectModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<ProjectModel>(API_PROJECTS_URL, project, {headers: httpHeaders});
    }

    getProjectById(projectId: number, token: string | null = null): Observable<ProjectModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.get<ProjectModel>(API_PROJECTS_URL + `/${projectId}`, {headers: httpHeaders});
    }

    getSchedulesByProjectId(projectId: number): Observable<ScheduleModel[]> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<ScheduleModel[]>(API_PROJECTS_URL + `/${projectId}/schedules`, {headers: httpHeaders});
    }

    getAnglesByProjectId(projectId: number, token: string | null = null): Observable<FileModel[]> {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.get<FileModel[]>(API_PROJECTS_URL + `/${projectId}/angles`, {headers: httpHeaders});
    }



    findProjects(queryParams: QueryParamsModel<ProjectModel>): Observable<QueryResultsModel<ProjectModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post(
            API_PROJECTS_URL + '/find',
            queryParams,
            {headers: httpHeaders}
        ).pipe(map<any, QueryResultsModel<ProjectModel>>((value) => {
            return new QueryResultsModel(value.items, value.totalCount, value.isReadStatus, '', value.totalPrice);
        }));
    }

    updateProject(project: ProjectModel, token: string | undefined | null): Observable<ProjectModel> {
        const httpHeader = this.httpUtils.getHTTPHeaders(token);
        return this.http.put<ProjectModel>(API_PROJECTS_URL + `/${project.id}`, project, {headers: httpHeader});
    }

    restoreProject(project: ProjectModel): Observable<OKResponse> {
        const httpHeader = this.httpUtils.getHTTPHeaders();
        return this.http.put<OKResponse>(API_PROJECTS_URL + `/${project.id}/restore`, project, {headers: httpHeader});
    }

    deleteProject(projectId: number): Observable<ProjectModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.delete<ProjectModel>(`${API_PROJECTS_URL}/${projectId}`, {headers: httpHeaders});
    }

    addUsersToProject(project: ProjectModel, users: number[]): Observable<ProjectModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const body = {userIds: users};
        return this.http.put<ProjectModel>(
            API_PROJECTS_URL + `/${project.id}/users`,
            body,
            {headers: httpHeaders});
    }

    deleteUserFromProject(project: ProjectModel, userId: number): Observable<ProjectModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.delete<ProjectModel>(
            `${API_PROJECTS_URL}/${project.id}/users/${userId}`,
            {headers: httpHeaders});
    }
    
    /**
     * SUUMO GO!のお客様向けのトークンでログインするための無名ユーザーのためのトークンを発行する
     */
    publishToken(projectId: number): Observable<string> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<{token: string}>(
            API_PROJECTS_URL + `/${projectId}/token`,
            {},
            {headers: httpHeaders}).pipe(map((value) => {
            return value.token;
        }));
    }

    formatInfo(data: any): string {
        if (data.diff) {
            let info = '';
            const previousNameDiff = this.formatDiffInfo(data.diff, 'previousName');
            if (previousNameDiff !== undefined) {
                info += '<p><strong>プロジェクト名</strong><br><del>' +
                    previousNameDiff +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newName') +
                    '</p>';
            }
            const previousFromDateDiff = this.formatDiffInfo(data.diff, 'previousFromDate');
            if (previousFromDateDiff !== undefined) {
                info += '<p><strong>着手日</strong><br><del>' +
                    previousFromDateDiff +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newFromDate') +
                    '</p>';
            }
            const previousScheduledDateOfDeliveryDiff = this.formatDiffInfo(data.diff, 'previousScheduledDateOfDelivery');
            if (previousScheduledDateOfDeliveryDiff !== undefined) {
                info += '<p><strong>納品予定日</strong><br><del>' +
                    previousScheduledDateOfDeliveryDiff +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newScheduledDateOfDelivery') +
                    '</p>';
            }
            const previousDateOfDeliveryDiff = this.formatDiffInfo(data.diff, 'previousDateOfDelivery');
            if (previousDateOfDeliveryDiff !== undefined) {
                info += '<p><strong>納品日</strong><br><del>' +
                    previousDateOfDeliveryDiff +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newDateOfDelivery') +
                    '</p>';
            }
            const previousTimeOfDelivery = this.formatDiffInfo(data.diff, 'previousTimeOfDelivery');
            if (previousTimeOfDelivery !== undefined) {
                info += '<p><strong>ご時間帯</strong><br><del>' +
                    this.formatDiffInfo(data.diff, 'previousTimeOfDelivery') +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newTimeOfDelivery') +
                    '</p>';
            }
            const previousStatus = this.formatDiffInfo(data.diff, 'previousStatus');
            if (previousStatus !== undefined) {
                info += '<p><strong>フェーズ</strong><br><del>' +
                    this.formatDiffInfo(data.diff, 'previousStatus') +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newStatus') + '</p>';
            }
            const usersAddedDiff = this.formatDiffInfo(data.diff, 'usersAdded');
            if (usersAddedDiff !== undefined) {
                info += '<p><strong>ユーザー追加</strong><br>' + usersAddedDiff + '</p>';
            }
            const usersRemovedDiff = this.formatDiffInfo(data.diff, 'usersRemoved');
            if (usersRemovedDiff !== undefined) {
                info += '<p><strong>ユーザー削除</strong><br>' + usersRemovedDiff + '</p>';
            }
            const fileAddedDiff = this.formatDiffInfo(data.diff, 'fileAdded');
            if (fileAddedDiff !== undefined) {
                info += '<p><strong>ファイル追加</strong><br>' + this.formatDiffInfo(data.diff, 'fileAdded') + '</p>';
            }
            const customerFileAddedDiff = this.formatDiffInfo(data.diff, 'customerFileAdded');
            if (customerFileAddedDiff !== undefined) {
                info += '<p><strong>お客様資料ファイルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'customerUrlAdded') != null) {
                info += '<p><strong>お客様資料URL追加</strong><br>' + this.formatDiffInfo(data.diff, 'customerUrlAdded') + '</p>';
            }
            if (this.formatDiffInfo(data.diff, 'angleFileAdded') != null) {
                info += '<p><strong>アングルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'draftFileAdded') != null) {
                info += '<p><strong>初稿ファイルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'secondDraftFileAdded') != null) {
                info += '<p><strong>第二稿ファイルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'thirdDraftFileAdded') != null) {
                info += '<p><strong>第三稿ファイルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'deliveryFileAdded') != null) {
                info += '<p><strong>入稿ファイルUP</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'angleSubmit') != null) {
                info += '<p><strong>アングル提出</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'angleSelected') != null) {
                info += '<p><strong>アングル選択</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'draftSubmit') != null) {
                info += '<p><strong>初稿ファイル提出</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'secondDraftSubmit') != null) {
                info += '<p><strong>第二稿ファイル提出</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'thirdDraftSubmit') != null) {
                info += '<p><strong>第三稿ファイル提出</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'annotationChanged') != null) {
                info += '<p><strong>チェックバック変更</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'annotationAdded') != null) {
                info += '<p><strong>チェックバック追加</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'deliveryOk') != null) {
                info += '<p><strong>入稿OK</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'deliverySubmit') != null) {
                info += '<p><strong>入稿ファイル提出</strong><br></p>';
            }
            if (this.formatDiffInfo(data.diff, 'scheduleAdded') != null) {
                info += '<p><strong>スケジュール追加</strong><br>' + this.formatDiffInfo(data.diff, 'scheduleAdded') + '</p>';
            }
            if (this.formatDiffInfo(data.diff, 'prevSchedule') != null) {
                info += '<p><strong>スケジュール編集</strong><br><del>' +
                    this.formatDiffInfo(data.diff, 'prevSchedule') +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newSchedule') +
                    '</p>';
            }
            if (this.formatDiffInfo(data.diff, 'previousPrice') != null) {
                info += '<p><strong>金額変更</strong><br><del>' +
                    this.formatDiffInfo(data.diff, 'previousPrice') +
                    '</del><br>' +
                    this.formatDiffInfo(data.diff, 'newPrice') +
                    '</p>';
            }
            if (data.operation === 'addProject') {

            } else if (data.operation === 'addConversation') {
                info += '<p><strong>グループチャット名</strong><br>' + (data.subject ? data.subject : data.subjectGeneratedFrom) + '</p>';
            } else if (data.operation === 'updateConversation') {
                const text = this.formatDiffInfo(data.diff, 'text');
                if (text) {
                    info += '<p><strong>グループチャット名</strong><br>' + text + '</p>';
                }
            } else if (data.operation === 'addMessage') {

            }

            return info;
        } else {
            return data.subject;
        }
    }

    formatDiffInfo(data: string, field: string): string | undefined {
        try {
            const newData = JSON.parse(data);
            if ((field.includes('date') || field.includes('Date')) && newData[field]) {
                return moment(newData[field]).format('YYYY-MM-DD');
            }
            if ((field.includes('time') || field.includes('Time')) && newData[field]) {
                const timeSplit = newData[field].split('..');
                return timeSplit[0] + ' to ' + timeSplit[1];
            }
            if (field.includes('usersAdded') && newData[field]) {
                return newData[field].length + '人';
            }
            if (field.includes('usersRemoved') && newData[field]) {
                return newData[field].length + '人';
            }
            if (field.includes('fileAdded') && newData[field]) {
                return '';
            }
            if (field.includes('scheduleAdded') && newData[field]) {
                return newData[field];
            }
            if (field.includes('prevSchedule') && newData[field]) {
                return newData[field];
            }
            if (field.includes('customerUrlAdded') && newData[field]) {
                return newData[field];
            }
            if (field.includes('Status') && newData[field] !== undefined) { // statusは0になりうる
                return PROJECT_STATUS_NAMES[newData[field] as ProjectStatus];
            }
            if (field.includes('Price') && newData[field]) {
                return '¥' + parseInt(newData[field], 10).toLocaleString();
            }
            return newData[field];
        } catch (e) {
            console.error(e);
            return data;
        }
    }

    isArchived(project: ProjectModel | undefined) {
        if (project === undefined) {
            return false;
        }
        return project.status === PROJECT_STATUS_ARCHIVED;
    }

    isSuumo(project: ProjectModel | undefined) {
        if (!project) {
            return false;
        }
        return project.createdBy.spaceType === SPACE_TYPE_SUUMO;
    }

    calendarDayTranslations(): string[] {
        return [
            this.translateService.instant('GENERAL.CALENDAR_DAY_0'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_1'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_2'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_3'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_4'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_5'),
            this.translateService.instant('GENERAL.CALENDAR_DAY_6')
        ];
    }

    preferredTimePriorities() {
        return PREFFERED_TIME_PRIORITIES;
    }

    getDocumentDefinition(project: ProjectModel, schedules: ScheduleModel[]): TDocumentDefinitions {
        return {
            info: {
                title: project.name + 'のスケジュール',
            },
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [0, 40, 0, 60],
            content: [
                {
                    image: this.titleImageUrl(project.name),
                    // 595.28, 841.89 -> default margins are 80?
                    fit: [761.89, 47.61],
                    margin: [40, 0, 40, 0]
                },
                {
                    image: this.scheduleDataUrl(),
                    // 595.28, 841.89 -> default margins are 80?
                    fit: [761.89, 47.61],
                    margin: [40, 0, 40, 0]
                },
                this.getPageData(schedules),
            ],
            footer: {
                alignment: 'center',
                image: studio55Logo,
                fit: [761.89, 11.9]
            },
            styles: {
                header: {
                    fontSize: 18,
                    bold: true,
                    margin: [0, 20, 0, 10],
                }
            }
        };
    }

    getPageData(schedules: ScheduleModel[]): Content[] {
        const itemPerPage = 15;
        const arrowSignPerPage = itemPerPage - 1;
        let fromIndex = 0;
        const scheduleModels = this.orderSchedules(schedules);
        let toIndex = scheduleModels.length > itemPerPage ? itemPerPage + arrowSignPerPage : 2 * scheduleModels.length - 1;
        const completeData: Content[] = [];
        const decidePageNumber = Math.ceil(scheduleModels.length / 15);
        for (let l = 0; l < decidePageNumber; l++) {
            const data = [{
                pageBreak: fromIndex !== 0 ? 'before' : '',
                columns: [
                    {width: '*' as const, text: '' as const},
                    {
                        width: 'auto' as const,
                        table: {
                            widths: this.getColumnWidths(fromIndex, toIndex),
                            body: [
                                this.getText(scheduleModels, 'toDate', fromIndex, toIndex),
                                this.getText(scheduleModels, 'timeOfDelivery', fromIndex, toIndex),
                                this.getTheSubject(scheduleModels, fromIndex, toIndex)
                            ]
                        }
                    },
                    {width: '*' as const, text: '' as const},
                ],
                margin: [20, 0, 20, 0] as [number, number, number, number],
            }] as [Content];
            completeData.push(data);
            fromIndex = toIndex + 1;
            if (2 * scheduleModels.length - 1 - toIndex <= 29) {
                toIndex = 2 * scheduleModels.length - 1;
            } else {
                toIndex = toIndex + 2 * itemPerPage;
            }
        }
        return completeData;
    }

    orderSchedules(schedules: ScheduleModel[]) {
        const newSchedules: ScheduleModel[] = [];
        for (const schedule of schedules) {
            const newSchedule = Object.assign({}, schedule);
            if (schedule.timeOfDelivery) {
                newSchedule.preferredTimePriority = this.preferredTimePriorities()[schedule.timeOfDelivery];
            } else {
                newSchedule.preferredTimePriority = 20;
            }
            newSchedules.push(newSchedule);
        }
        return newSchedules.sort((a, b) => {
            const dayA = moment(a.fromDate).format('YYYYMMDD');
            const dayB = moment(b.fromDate).format('YYYYMMDD');
            if (dayA > dayB) {
                return 1;
            }
            if (dayA < dayB) {
                return -1;
            }
            if (a.preferredTimePriority !== null &&
                a.preferredTimePriority !== undefined &&
                b.preferredTimePriority !== null &&
                b.preferredTimePriority !== undefined) {
                if (a.preferredTimePriority > b.preferredTimePriority) {
                    return 1;
                }
                if (b.preferredTimePriority < b.preferredTimePriority) {
                    return -1;
                }
            }
            return a.priority - b.priority;
        });
    }

    getColumnWidths(start: number, end: number): ("auto" | "*")[]  {
        const widths: ("auto" | "*")[] = [];
        for (let i = start; i < end; i++) {
            if (i % 2) {
                widths.push('auto');
            } else {
                widths.push('*');
            }
        }
        return widths;
    }

    toDayOfTheWeek(dateString: string) {
        const date = Date.parse(dateString);
        return this.calendarDayTranslations()[new Date(date).getDay()];
    }

    getText(scheduleList: ScheduleModel[], field: keyof ScheduleModel, fromIndex: number, toIndex: number) {
        const text: {
            text?: string;
            image?: string | undefined;
            fit?: [number, number];
            margin: [number, number, number, number];
            border: [boolean, boolean, boolean, boolean];
            fillColor?: string;
        }[] = [];
        for (let i = fromIndex; i < toIndex; i++) {
            let t1: string = '';
            let t2 = '';
            if (i === 0) {
                if (scheduleList[i][field]) {
                    if (field === 'timeOfDelivery') {
                        const timeOfDelivery = scheduleList[i / 2][field];
                        if (timeOfDelivery && timeOfDelivery.includes('..')) {
                            const timeList = timeOfDelivery.split('..');
                            t1 = timeList[0] + ':00 ~ ' + timeList[1] + ':00';
                        } else {
                            t1 = scheduleList[i][field] + ':00';
                        }
                    } else {
                        t1 = this.datePipe.transform(scheduleList[i][field] as string, 'MM/dd') || "";
                        t2 = this.toDayOfTheWeek(scheduleList[i][field] as string);
                    }
                }
                if (t1 !== '' && t2 !== '') {
                    text.push({
                        image: this.twoLinesDataUrl(t1, t2),
                        fit: [50, 25],
                        margin: [2, 2, 2, 2],
                        border: [false, false, false, false],
                        fillColor: '#F4F5F8'
                    });
                } else {
                    text.push({
                        image: this.oneLineDataUrl(t1),
                        fit: [50, 28],
                        margin: [2, 2, 2, 2],
                        border: [false, false, false, false],
                        fillColor: '#F4F5F8'
                    });
                }
            } else if (i % 2 === 1) {
                text.push({
                    text: '  ',
                    border: [false, false, false, false],
                    margin: [4, 4, 4, 4],
                });
            } else if (i % 2 === 0) {
                if (scheduleList[i / 2][field]) {
                    if (field === 'timeOfDelivery') {
                        const timeOfDelivery = scheduleList[i / 2][field];
                        if (timeOfDelivery && timeOfDelivery.includes('..')) {
                            const timeList = timeOfDelivery.split('..');
                            t1 = timeList[0] + ':00 ~ ' + timeList[1] + ':00';
                        } else {
                            t1 = scheduleList[i / 2][field] + ':00';
                        }
                    } else {
                        t1 = this.datePipe.transform(scheduleList[i / 2][field] as string, 'MM/dd') || "";
                        t2 = this.toDayOfTheWeek(scheduleList[i / 2][field] as string);
                    }
                }
                if (t1 !== '' && t2 !== '') {
                    text.push({
                        image: this.twoLinesDataUrl(t1, t2),
                        fit: [50, 28],
                        margin: [2, 2, 2, 2],
                        border: [false, false, false, false],
                        fillColor: '#F4F5F8'
                    });
                } else {
                    text.push({
                        image: this.oneLineDataUrl(t1),
                        fit: [50, 14],
                        margin: [2, 2, 2, 2],
                        border: [false, false, false, false],
                        fillColor: '#F4F5F8'
                    });
                }
            }
        }
        return text;
    }

    getTheSubject(scheduleList: ScheduleModel[], fromIndex: number, toIndex: number) {
        const subject: {
            image: string | undefined;
            fit: [number, number];
            alignment: 'center';
            border: [boolean, boolean, boolean, boolean];
            fillColor?: string;
            margin?: [number, number, number, number];
        }[] = [];
        for (let i = fromIndex; i < toIndex; i++) {
            if (i === 0) {
                subject.push({
                    image: this.verticallyRotatedText(scheduleList[i].subject),
                    fit: [50, 300],
                    alignment: 'center',
                    border: [false, false, false, false],
                    fillColor: scheduleList[i].color === 'blue' ? '#c5d9f1' : '#F2DCDB',
                });
            } else if (i % 2 === 1) {
                subject.push({
                    image: this.arrowDataUrl(),
                    fit: [10, 10],
                    alignment: 'center',
                    border: [false, false, false, false],
                    margin: [0, 80, 0, 0],
                });
            } else if (i % 2 === 0) {
                subject.push({
                    image: this.verticallyRotatedText(scheduleList[i / 2].subject),
                    fit: [50, 300],
                    alignment: 'center',
                    border: [false, false, false, false],
                    fillColor: scheduleList[i / 2].color === 'blue' ? '#c5d9f1' : '#F2DCDB',
                });
            }
        }
        return subject;
    }

    verticallyRotatedText(text: string) {
        const canvas = document.createElement('canvas');
        canvas.width = 40;
        canvas.height = 720;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.font = '28px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        this.fillVerticalText(ctx, text, 20, 20, 40);
        return canvas.toDataURL();
    }

    fillVerticalText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, verticalSpacing: number) {
        for (let i = 0; i < text.length; i++) {
            if (text[i].charCodeAt(0) === 12540) {
                ctx.fillText('|', x, y + i * verticalSpacing);
            } else {
                ctx.fillText(text[i], x, y + i * verticalSpacing);
            }
        }
    }

    titleImageUrl(title: string) {
        const canvas = document.createElement('canvas');
        canvas.width = 1600;
        canvas.height = 100;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        let size = 60;
        if (title.length > 25) {
            size = 40;
        }
        if (title.length > 40) {
            size = 20;
        }
        ctx.font = `${size}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(title, 800, 50);
        return canvas.toDataURL();
    }

    oneLineDataUrl(line1: string) {
        const canvas = document.createElement('canvas');
        canvas.width = 100;
        canvas.height = 28;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(line1, 50, 14);
        return canvas.toDataURL();
    }

    twoLinesDataUrl(line1: string, line2: string) {
        const canvas = document.createElement('canvas');
        canvas.width = 100;
        canvas.height = 57;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(line1, 50, 14);
        ctx.fillText(line2, 50, 50);
        return canvas.toDataURL();
    }

    scheduleDataUrl() {
        const schedule = this.translateService.instant('MENU.SCHEDULE');
        const canvas = document.createElement('canvas');
        canvas.width = 1600;
        canvas.height = 100;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.font = '40px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(schedule, 800, 30);
        return canvas.toDataURL();
    }

    arrowDataUrl() {
        const canvas = document.createElement('canvas');
        canvas.width = 30;
        canvas.height = 30;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'black';
        ctx.font = '30px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('→', 15, 15);
        return canvas.toDataURL();
    }
}
